<template>
  <button v-bind:class="classes" @click="onButtonClick" :aria-label='ariaLabel' :is="displayAs">
    <icon
      class="c-button__icon-before u-hide-mobile"
      width="14"
      height="14"
      v-if="iconBefore"
      :name="iconBefore"
      :fill="iconBeforeFill"
      :aria-label='ariaLabel'
    ></icon>
    <slot></slot>
    <span v-if="buttonText">{{buttonText}}</span>
    <icon
      class="c-button__icon-after u-hide-mobile"
      width="14"
      height="14"
      v-if="iconAfter"
      :name="iconAfter"
      :fill="iconAfterFill"
      :aria-label='ariaLabel'
    ></icon>
  </button>
</template>

<script>
export default {
  name: "base-button",

  props: {
    iconBefore: String,
    iconAfter: String,
    primary: [String, Boolean],
    danger: [Boolean],
    dangerInvert: [Boolean],
    outline: [Boolean],
    success: [Boolean],
    successInvert: [Boolean],
    block: Boolean,
    inline: Boolean,
    square: Boolean,
    buttonText: String,
    buttonLink: String,
    ghost: Boolean,
    asLink: {
      type: Boolean,
      default: false
    },
    isLink: {
      type: Boolean,
      default: false
    },
    iconBeforeFill: {
      type: Boolean,
      default: true
    },
    iconAfterFill: {
      type: Boolean,
      default: true
    },
    warning: {
      type: Boolean,
      default: false
    },
    mobileFullWidth: {
      type: Boolean,
      default: false
    },
    ariaLabel:String
  },

  computed: {
    classes() {
      return {
        "c-button": true,
        "c-button--primary": this.isPrimary,
        "c-button--danger": this.danger,
        "c-button--success": this.success,
        "c-button--block": this.block,
        "c-button--square": this.square,
        "c-button--ghost": this.ghost,
        "c-button--warning": this.warning,
        "has-icon-before": this.iconBefore,
        "has-icon-after": this.iconAfter,
        "c-button--inline": this.inline,
        "c-button--success--invert": this.successInvert,
        "c-button--danger--invert": this.dangerInvert,
        "c-button--outline": this.outline,
        "c-button--mobile-full-width": this.mobileFullWidth,
        "c-button--is-link": this.isLink,
        asLink: this.asLink
      };
    },

    displayAs() {
      return this.isLink ? 'a' : 'button';
    },

    isPrimary() {
      if (this.primary == 1) {
        return true;
      }
      if (this.primary == 0) {
        return false;
      }

      return this.primary;
    }
  },

  methods: {
    onButtonClick() {
      if (this.buttonLink && this.buttonLink != 'Click to Edit') {
        window.location.href = this.buttonLink;
        return;
      }
      this.$emit("click");
    }
  }
};
</script>

<style lang="scss">
.c-button {
  display: inline-block;
  background: $colour-nhs-white;
  padding: 1em 1em;
  @media (min-width: 640px) {
    padding: 1em 1.25em;
  }
  border: 1px solid $colour-nhs-pale-grey;
  color: $colour-nhs-dark-grey;
  border-radius: 2em;
  font-size: 16px;
  cursor: pointer;

  &.has-icon-before,
  &.has-icon-after {
    display: inline-flex;
    align-items: center;
  }

  &--outline {
    background: transparent;
    border: 1px solid $colour-nhs-white;
    color: $colour-nhs-white;

    &:hover {
      background: $colour-nhs-white;
      border-color: $colour-nhs-white;
    }
  }

  &:not(:last-of-type) {
    margin-right: 0.5em;
  }

  &__icon-before {
    margin-right: 0.5em;
  }
  &__icon-after {
    margin-left: 0.5em;
  }

  &:hover {
    color: $colour-nhs-blue;
    border: 1px solid $colour-nhs-blue;
  }

  &:disabled {
    opacity: 0.5;
  }

  &--mobile-full-width {
    width: 100vw;
    margin-left:-10px;
    box-sizing: border-box;

    @media (min-width: 768px) {
      width: auto;
      margin-left: auto;
    }
  }

  &--primary {
    background: $colour-nhs-blue;
    color: $colour-nhs-white;
    border-color: transparent;

    &:hover {
      background: darken($colour-nhs-blue, 5%);
      color: $colour-nhs-white;
      border-color: transparent;
    }

    svg {
      path {
        stroke: $colour-nhs-core-white;
        fill: $colour-nhs-core-white;
      }
    }
  }

  &--danger {
    background: $colour-nhs-emergency-red;
    color: $colour-nhs-white;
    border-color: transparent;

    &:hover {
      background: darken($colour-nhs-emergency-red, 5%);
      color: $colour-nhs-white;
      border-color: transparent;
    }

    &--invert {
      color: $colour-nhs-emergency-red;
      background: $colour-nhs-white;

      &:hover {
        color: darken($colour-nhs-emergency-red, 5%);
        background: $colour-nhs-white;
        border: 1px solid $colour-nhs-pale-grey;
      }
    }
  }

  &--success {
    background: #0cce4e;
    color: $colour-nhs-white;
    border-color: transparent;

    &:hover {
      background: darken(#0cce4e, 5%);
      color: $colour-nhs-white;
      border-color: transparent;
    }

    &--invert {
      color: #0cce4e;
      background: $colour-nhs-white;

      &:hover {
        color: darken(#0cce4e, 5%);
        background: $colour-nhs-white;
        border: 1px solid $colour-nhs-pale-grey;
      }
    }
  }

  &--warning {
    background: $colour-nhs-orange;
    color: $colour-nhs-core-white;
    border-color: transparent;
    svg {
      path {
        stroke: $colour-nhs-core-white;
      }
      &.u-hide-mobile {
        display: none;
      }
    }

    &:hover {
      border-color: transparent;
      color: $colour-nhs-core-white;
      background: darken($colour-nhs-orange, 5%);
    }
  }

  &--block {
    display: block;
    width: 100%;
  }

  &--square {
    border-radius: 0;
  }

  &--inline {
    border-radius: 4px;
    padding: 10px;
    font-size: 14px;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.18);
  }

  &--ghost,
  &--ghost:hover {
    background: none;
    border: none;
    outline: none;
    box-shadow: none;
  }

  &.asLink {
    background: transparent;
    display: inline;
    border: none;
    padding: 0;
    border-radius: 0;
    color: $colour-nhs-core-blue;

    &:hover {
      text-decoration: underline;
    }
  }

  &--is-link {
    text-decoration: none;
  }

  svg {
    &.u-hide-mobile {
      display: none;

      @media screen and(min-width: 1024px) {
        display: inline-block;
      }
    }
  }
}
</style>
